import { StructureAnnualForecastSchema } from "./structure.js";
import { Type, type Static } from "@sinclair/typebox";

const UuidSchema = Type.String({ format: "uuid" });
const IsoDateTimeSchema = Type.String({ format: "date-time" });
const IsoDateSchema = Type.String({ format: "date" });

const NullableString = Type.Union([Type.String(), Type.Null()]);
const NullableUuid = Type.Union([UuidSchema, Type.Null()]);
const NullableIsoDate = Type.Union([IsoDateSchema, Type.Null()]);

export const FicClientProfileSchema = Type.Object(
  {
    type: Type.Optional(
      Type.Union([
        Type.Literal("company"),
        Type.Literal("person"),
        Type.Literal("pa"),
        Type.Literal("condo"),
      ]),
    ),
    firstName: Type.Optional(NullableString),
    lastName: Type.Optional(NullableString),
    contactPerson: Type.Optional(NullableString),
    certifiedEmail: Type.Optional(NullableString),
    sdiCode: Type.Optional(NullableString),
    addressStreet: Type.Optional(NullableString),
    addressPostalCode: Type.Optional(NullableString),
    addressCity: Type.Optional(NullableString),
    addressProvince: Type.Optional(NullableString),
    addressExtra: Type.Optional(NullableString),
    country: Type.Optional(NullableString),
    countryIso: Type.Optional(NullableString),
    fax: Type.Optional(NullableString),
    notes: Type.Optional(NullableString),
    defaultPaymentTerms: Type.Optional(
      Type.Union([Type.Integer({ minimum: 0, maximum: 3650 }), Type.Null()]),
    ),
    defaultPaymentTermsType: Type.Optional(
      Type.Union([
        Type.Literal("standard"),
        Type.Literal("end_of_month"),
        Type.Null(),
      ]),
    ),
    bankName: Type.Optional(NullableString),
    bankIban: Type.Optional(NullableString),
    bankSwiftCode: Type.Optional(NullableString),
    shippingAddress: Type.Optional(NullableString),
    eInvoice: Type.Optional(Type.Boolean()),
    priceListId: Type.Optional(NullableString),
    defaultDiscount: Type.Optional(
      Type.Union([Type.Number({ minimum: 0, maximum: 100 }), Type.Null()]),
    ),
  },
  { additionalProperties: false },
);

export const DirectoryQuerySchema = Type.Object(
  {
    search: Type.Optional(Type.String({ maxLength: 160 })),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 512 })),
    limit: Type.Optional(
      Type.Integer({ minimum: 1, maximum: 200, default: 50 }),
    ),
    status: Type.Optional(Type.String({ maxLength: 40 })),
    administratorId: Type.Optional(UuidSchema),
    administratorAssignment: Type.Optional(
      Type.Union([Type.Literal("assigned"), Type.Literal("unassigned")]),
    ),
    administratorClientId: Type.Optional(UuidSchema),
  },
  { additionalProperties: false },
);

export const IdParamsSchema = Type.Object(
  { id: UuidSchema },
  { additionalProperties: false },
);

export const ClientSchema = Type.Object(
  {
    id: UuidSchema,
    legacyUid: NullableString,
    kind: Type.Union([
      Type.Literal("person"),
      Type.Literal("company"),
      Type.Literal("public-body"),
      Type.Literal("other"),
    ]),
    businessName: Type.String(),
    taxCode: NullableString,
    vatNumber: NullableString,
    email: NullableString,
    phone: NullableString,
    administratorId: NullableUuid,
    administratorName: NullableString,
    commercialId: NullableUuid,
    commercialContact: NullableString,
    ficProfile: FicClientProfileSchema,
    ficExternalId: NullableString,
    status: Type.Union([
      Type.Literal("active"),
      Type.Literal("inactive"),
      Type.Literal("deleted"),
    ]),
    condominiumCount: Type.Integer({ minimum: 0 }),
    version: Type.Integer({ minimum: 1 }),
    updatedAt: IsoDateTimeSchema,
  },
  { additionalProperties: false },
);

export const CondominiumSchema = Type.Object(
  {
    id: UuidSchema,
    legacyUid: NullableString,
    name: Type.String(),
    taxCode: NullableString,
    administratorId: NullableUuid,
    administratorClientId: NullableUuid,
    administratorName: NullableString,
    commercialId: NullableUuid,
    commercialContact: NullableString,
    billingClientId: NullableUuid,
    notes: NullableString,
    status: Type.Union([Type.Literal("active"), Type.Literal("deleted")]),
    version: Type.Integer({ minimum: 1 }),
    updatedAt: IsoDateTimeSchema,
  },
  { additionalProperties: false },
);

export const AdministratorSchema = Type.Object(
  {
    id: UuidSchema,
    code: Type.String({ minLength: 1, maxLength: 120 }),
    displayOrder: Type.Integer({ minimum: 1 }),
    ficRevenueCenter: Type.String({ minLength: 1, maxLength: 120 }),
    active: Type.Boolean(),
    clientCount: Type.Integer({ minimum: 0 }),
    condominiumCount: Type.Integer({ minimum: 0 }),
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const CommercialSchema = Type.Object(
  {
    id: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 160 }),
    displayOrder: Type.Integer({ minimum: 1 }),
    active: Type.Boolean(),
    clientCount: Type.Integer({ minimum: 0 }),
    condominiumCount: Type.Integer({ minimum: 0 }),
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const DirectoryReferenceQuerySchema = Type.Object(
  { includeInactive: Type.Optional(Type.Boolean({ default: false })) },
  { additionalProperties: false },
);

export const AdministratorCreateSchema = Type.Object(
  {
    operationId: UuidSchema,
    code: Type.String({ minLength: 1, maxLength: 120 }),
  },
  { additionalProperties: false },
);

export const CommercialCreateSchema = Type.Object(
  {
    operationId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 160 }),
  },
  { additionalProperties: false },
);

export const DirectoryReferenceStatusUpdateSchema = Type.Object(
  {
    operationId: UuidSchema,
    version: Type.Integer({ minimum: 1 }),
    active: Type.Boolean(),
  },
  { additionalProperties: false },
);

export const EmployeeSchema = Type.Object(
  {
    archivedAt: Type.Optional(NullableString),
    appAccount: Type.Optional(
      Type.Union([
        Type.Null(),
        Type.Object({
          id: UuidSchema,
          username: Type.String(),
          temporaryPassword: Type.Optional(Type.String()),
        }),
      ]),
    ),
    id: UuidSchema,
    legacyUid: NullableString,
    employeeCode: NullableString,
    firstName: Type.String(),
    lastName: Type.String(),
    taxCode: NullableString,
    email: NullableString,
    phone: NullableString,
    hiredOn: NullableIsoDate,
    endedOn: NullableIsoDate,
    role: NullableString,
    contractExpiresOn: NullableIsoDate,
    notes: NullableString,
    contractHourlyCost: NullableString,
    status: Type.Union([
      Type.Literal("active"),
      Type.Literal("former"),
      Type.Literal("suspended"),
    ]),
    documentCount: Type.Integer({ minimum: 0 }),
    certificateCount: Type.Integer({ minimum: 0 }),
    version: Type.Integer({ minimum: 1 }),
    updatedAt: IsoDateTimeSchema,
  },
  { additionalProperties: false },
);

export const SupplierSchema = Type.Object(
  {
    id: UuidSchema,
    legacyUid: NullableString,
    businessName: Type.String(),
    taxCode: NullableString,
    vatNumber: NullableString,
    email: NullableString,
    phone: NullableString,
    status: Type.Union([
      Type.Literal("active"),
      Type.Literal("inactive"),
      Type.Literal("deleted"),
    ]),
    documentCount: Type.Integer({ minimum: 0 }),
    paymentCount: Type.Integer({ minimum: 0 }),
    version: Type.Integer({ minimum: 1 }),
    updatedAt: IsoDateTimeSchema,
  },
  { additionalProperties: false },
);

export const DocumentSchema = Type.Object(
  {
    id: UuidSchema,
    fileName: Type.String(),
    mediaType: NullableString,
    sizeBytes: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    sha256: NullableString,
    rootKey: NullableString,
    relativePath: NullableString,
    sourceModifiedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
    verifiedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  },
  { additionalProperties: false },
);

export const DocumentRootSchema = Type.Object(
  {
    id: UuidSchema,
    rootKey: Type.String(),
    displayName: Type.String(),
    status: Type.String(),
    fileCount: Type.Integer({ minimum: 0 }),
    totalBytes: Type.Integer({ minimum: 0 }),
    manifestSha256: NullableString,
    scannedAt: Type.Union([IsoDateTimeSchema, Type.Null()]),
  },
  { additionalProperties: false },
);

const page = <T extends ReturnType<typeof Type.Object>>(item: T) =>
  Type.Object(
    {
      items: Type.Array(item),
      nextCursor: NullableString,
    },
    { additionalProperties: false },
  );

export const ClientPageSchema = page(ClientSchema);
export const CondominiumPageSchema = page(CondominiumSchema);
export const AdministratorPageSchema = page(AdministratorSchema);
export const CommercialPageSchema = page(CommercialSchema);
export const EmployeePageSchema = page(EmployeeSchema);
export const SupplierPageSchema = page(SupplierSchema);
export const DocumentPageSchema = page(DocumentSchema);

const ContactFields = {
  taxCode: Type.Optional(NullableString),
  vatNumber: Type.Optional(NullableString),
  email: Type.Optional(NullableString),
  phone: Type.Optional(NullableString),
};

export const ClientCreateSchema = Type.Object(
  {
    operationId: UuidSchema,
    kind: Type.Union([
      Type.Literal("person"),
      Type.Literal("company"),
      Type.Literal("public-body"),
      Type.Literal("other"),
    ]),
    businessName: Type.String({ minLength: 1, maxLength: 240 }),
    ...ContactFields,
    administratorId: Type.Optional(NullableUuid),
    commercialId: Type.Optional(NullableUuid),
    commercialContact: Type.Optional(NullableString),
    ficProfile: Type.Optional(FicClientProfileSchema),
  },
  { additionalProperties: false },
);

export const ClientUpdateSchema = Type.Object(
  {
    operationId: UuidSchema,
    version: Type.Integer({ minimum: 1 }),
    kind: Type.Optional(ClientCreateSchema.properties.kind),
    businessName: Type.Optional(Type.String({ minLength: 1, maxLength: 240 })),
    ...ContactFields,
    administratorId: Type.Optional(NullableUuid),
    commercialId: Type.Optional(NullableUuid),
    commercialContact: Type.Optional(NullableString),
    ficProfile: Type.Optional(FicClientProfileSchema),
    status: Type.Optional(ClientSchema.properties.status),
  },
  { additionalProperties: false, minProperties: 2 },
);

export const ClientDeleteSchema = Type.Object(
  {
    operationId: UuidSchema,
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const CustomerSchema = Type.Object(
  {
    ...ClientSchema.properties,
    clientId: NullableUuid,
    condominiumId: NullableUuid,
    revision: Type.String({ pattern: "^[a-f0-9]{64}$" }),
    identityReview: Type.Boolean(),
    assignmentConflict: Type.Boolean(),
  },
  { additionalProperties: false },
);
export const CustomerUpdateSchema = Type.Object(
  {
    operationId: UuidSchema,
    revision: CustomerSchema.properties.revision,
    administratorId: Type.Optional(UuidSchema),
    commercialId: Type.Optional(NullableUuid),
    taxCode: Type.Optional(NullableString),
  },
  { additionalProperties: false, minProperties: 3 },
);
export const CustomerDeleteSchema = Type.Object(
  {
    operationId: UuidSchema,
    revision: CustomerSchema.properties.revision,
  },
  { additionalProperties: false },
);
export const CustomerLinkSchema = Type.Object(
  {
    operationId: UuidSchema,
    revision: CustomerSchema.properties.revision,
    clientId: UuidSchema,
    clientRevision: CustomerSchema.properties.revision,
    administratorId: UuidSchema,
    commercialId: NullableUuid,
    taxCode: NullableString,
  },
  { additionalProperties: false },
);
export type Customer = Static<typeof CustomerSchema>;
export type CustomerUpdate = Static<typeof CustomerUpdateSchema>;
export type CustomerDelete = Static<typeof CustomerDeleteSchema>;
export type CustomerLink = Static<typeof CustomerLinkSchema>;

export const CondominiumCreateSchema = Type.Object(
  {
    operationId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 240 }),
    taxCode: Type.Optional(NullableString),
    administratorId: UuidSchema,
    administratorClientId: Type.Optional(NullableUuid),
    commercialId: Type.Optional(NullableUuid),
    commercialContact: Type.Optional(NullableString),
    billingClientId: Type.Optional(NullableUuid),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false },
);

export const CondominiumUpdateSchema = Type.Object(
  {
    operationId: UuidSchema,
    version: Type.Integer({ minimum: 1 }),
    name: Type.Optional(Type.String({ minLength: 1, maxLength: 240 })),
    taxCode: Type.Optional(NullableString),
    administratorId: Type.Optional(NullableUuid),
    administratorClientId: Type.Optional(NullableUuid),
    commercialId: Type.Optional(NullableUuid),
    commercialContact: Type.Optional(NullableString),
    billingClientId: Type.Optional(NullableUuid),
    notes: Type.Optional(NullableString),
  },
  { additionalProperties: false, minProperties: 2 },
);

export const CondominiumDeleteSchema = Type.Object(
  {
    operationId: UuidSchema,
    version: Type.Integer({ minimum: 1 }),
  },
  { additionalProperties: false },
);

export const EmployeeCreateSchema = Type.Object(
  {
    operationId: UuidSchema,
    employeeCode: Type.Optional(NullableString),
    firstName: Type.String({ minLength: 1, maxLength: 120 }),
    lastName: Type.String({ minLength: 1, maxLength: 120 }),
    taxCode: Type.Optional(NullableString),
    email: Type.Optional(NullableString),
    phone: Type.Optional(NullableString),
    hiredOn: Type.Optional(NullableIsoDate),
    endedOn: Type.Optional(NullableIsoDate),
    role: Type.Optional(NullableString),
    contractExpiresOn: Type.Optional(NullableIsoDate),
    notes: Type.Optional(NullableString),
    contractHourlyCost: Type.Optional(
      Type.Union([
        Type.Number({ minimum: 0, maximum: 999999.9999 }),
        Type.Null(),
      ]),
    ),
    status: Type.Optional(EmployeeSchema.properties.status),
  },
  { additionalProperties: false },
);

export const EmployeeUpdateSchema = Type.Object(
  {
    version: Type.Integer({ minimum: 1 }),
    employeeCode: Type.Optional(NullableString),
    firstName: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
    lastName: Type.Optional(Type.String({ minLength: 1, maxLength: 120 })),
    taxCode: Type.Optional(NullableString),
    email: Type.Optional(NullableString),
    phone: Type.Optional(NullableString),
    hiredOn: Type.Optional(NullableIsoDate),
    endedOn: Type.Optional(NullableIsoDate),
    role: Type.Optional(NullableString),
    contractExpiresOn: Type.Optional(NullableIsoDate),
    notes: Type.Optional(NullableString),
    contractHourlyCost: Type.Optional(
      Type.Union([
        Type.Number({ minimum: 0, maximum: 999999.9999 }),
        Type.Null(),
      ]),
    ),
    status: Type.Optional(EmployeeSchema.properties.status),
  },
  { additionalProperties: false, minProperties: 2 },
);

export const EmployeeMonthlyCostQuerySchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200, default: 2026 }),
    ),
    month: Type.Optional(Type.Integer({ minimum: 1, maximum: 12 })),
  },
  { additionalProperties: false },
);

export const EmployeeCostOverviewQuerySchema = Type.Object(
  {
    managementYear: Type.Optional(
      Type.Integer({ minimum: 2000, maximum: 2200, default: 2026 }),
    ),
    asOf: Type.Optional(IsoDateSchema),
  },
  { additionalProperties: false },
);

export const EmployeeCostOverviewItemSchema = Type.Object(
  {
    archivedAt: Type.Optional(NullableString),
    costBasis: Type.Optional(
      Type.Object(
        {
          hourlyCost: NullableString,
          source: Type.Union([
            Type.Literal("payroll"),
            Type.Literal("mixed"),
            Type.Literal("contract"),
            Type.Literal("missing"),
          ]),
          payrollHours: Type.String(),
          projectedHours: Type.Optional(Type.String()),
          estimatedHours: Type.Optional(Type.String()),
          managementHours: Type.Optional(Type.String()),
          projectionThrough: Type.Optional(NullableIsoDate),
          payrollCost: NullableString,
          projectedPayrollCost: Type.Optional(NullableString),
          payrollMonths: Type.Integer({ minimum: 0 }),
          documentedCostMonths: Type.Integer({ minimum: 0 }),
          through: NullableIsoDate,
        },
        { additionalProperties: false },
      ),
    ),
    employeeId: UuidSchema,
    employeeName: Type.String(),
    status: EmployeeSchema.properties.status,
    role: NullableString,
    contractExpiresOn: NullableIsoDate,
    contractHourlyCost: NullableString,
    employeeWorkedHours: Type.String(),
    internalHourlyAllocation: NullableString,
    appliedHourlyCost: NullableString,
    documentCount: Type.Integer({ minimum: 0 }),
    certificateCount: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);

export const EmployeeStructureWeekSchema = Type.Object(
  {
    structureForecast: Type.Optional(StructureAnnualForecastSchema),
    weekStart: IsoDateSchema,
    weekEnd: IsoDateSchema,
    status: Type.Union([
      Type.Literal("available"),
      Type.Literal("no-hours"),
      Type.Literal("undated-hours"),
      Type.Literal("undated-costs"),
      Type.Literal("missing-basis"),
      Type.Literal("costs-unreconciled"),
    ]),
    payrollThrough: Type.Optional(NullableIsoDate),
    estimatedHours: Type.Optional(Type.String()),
    managementHours: Type.Optional(Type.String()),
    incompletePayrollEmployees: Type.Optional(Type.Integer({ minimum: 0 })),
    structureCostMin: Type.Optional(NullableString),
    structureCostMax: Type.Optional(NullableString),
    hourlyAllocation: NullableString,
    recordedHours: Type.String(),
    cumulativeHours: NullableString,
    projectedHours: NullableString,
    structureCost: NullableString,
    fixedBudgetCost: Type.String(),
    fallbackCost: NullableString,
    previousWeekStart: NullableIsoDate,
    costEffect: NullableString,
    hoursEffect: NullableString,
    totalChange: NullableString,
    baselineWeekStart: NullableIsoDate,
    baselineCostEffect: NullableString,
    baselineHoursEffect: NullableString,
    baselineChange: NullableString,
    hourlyAllocationMin: Type.Optional(NullableString),
    hourlyAllocationMax: Type.Optional(NullableString),
    cumulativeHoursMin: Type.Optional(NullableString),
    cumulativeHoursMax: Type.Optional(NullableString),
    projectedHoursMin: Type.Optional(NullableString),
    projectedHoursMax: Type.Optional(NullableString),
    unplacedOpeningHours: Type.Optional(Type.String()),
  },
  { additionalProperties: false },
);
export const EmployeeStructureHistorySchema = Type.Object(
  {
    structureForecast: Type.Optional(StructureAnnualForecastSchema),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    asOf: IsoDateSchema,
    method: Type.Union([
      Type.Literal("restated-current-budget-projected"),
      Type.Literal("actual-payroll-projected"),
      Type.Literal("previous-year-actual-payroll-projected"),
    ]),
    payrollThrough: Type.Optional(NullableIsoDate),
    weeks: Type.Array(EmployeeStructureWeekSchema, { maxItems: 54 }),
    openingBalanceHours: Type.String(),
    undatedOpeningHours: Type.String(),
    inheritedOpeningHours: Type.Optional(Type.String()),
    hoursAvailableFrom: NullableIsoDate,
    costsAvailableFrom: NullableIsoDate,
    undatedCost: Type.String(),
    excludedFutureCost: Type.String(),
    fixedBudgetCost: Type.String(),
    currentStructureCost: Type.String(),
    unreconciledCenters: Type.Integer({ minimum: 0 }),
  },
  { additionalProperties: false },
);
export type EmployeeStructureWeek = Static<typeof EmployeeStructureWeekSchema>;
export type EmployeeStructureHistory = Static<
  typeof EmployeeStructureHistorySchema
>;

export const EmployeeDossierSchema = Type.Object(
  {
    documents: Type.Array(
      Type.Object(
        {
          id: UuidSchema,
          fileName: Type.String(),
          kind: Type.String(),
          validUntil: NullableIsoDate,
        },
        { additionalProperties: false },
      ),
    ),
    certificates: Type.Array(
      Type.Object(
        {
          id: UuidSchema,
          name: Type.String(),
          issuer: NullableString,
          issuedOn: NullableIsoDate,
          expiresOn: NullableIsoDate,
          status: Type.String(),
          fileName: NullableString,
          reviewReason: Type.Optional(NullableString),
          expiryNotRequired: Type.Optional(Type.Boolean()),
          courseRecognized: Type.Optional(
            Type.Union([Type.Boolean(), Type.Null()]),
          ),
        },
        { additionalProperties: false },
      ),
    ),
  },
  { additionalProperties: false },
);
export type EmployeeDossier = Static<typeof EmployeeDossierSchema>;

export const EmployeeOwnerPerformanceSchema = Type.Object(
  {
    employeeId: UuidSchema,
    employeeName: Type.String(),
    jobCount: Type.Integer({ minimum: 0 }),
    entrustedValue: Type.String(),
    teamHours: Type.String(),
    ownerHours: Type.String(),
    collaboratorHours: Type.String(),
    externalCosts: Type.String(),
    laborCost: Type.String(),
    netResult: Type.String(),
    profitPerHour: NullableString,
    marginPercent: NullableString,
    ownerHoursCoveragePercent: NullableString,
    coveredJobCount: Type.Integer({ minimum: 0 }),
    jobHoursCoveragePercent: NullableString,
  },
  { additionalProperties: false },
);

export const EmployeeCostOverviewSchema = Type.Object(
  {
    structureForecast: Type.Optional(StructureAnnualForecastSchema),
    averageHourlyCost: Type.Optional(NullableString),
    payrollTotalCost: Type.Optional(NullableString),
    projectedPayrollTotalCost: Type.Optional(NullableString),
    payrollThrough: Type.Optional(NullableIsoDate),
    estimatedHours: Type.Optional(Type.String()),
    activeEmployeeCount: Type.Optional(Type.Integer({ minimum: 0 })),
    costedActiveEmployeeCount: Type.Optional(Type.Integer({ minimum: 0 })),
    appliedHourlyAllocation: Type.Optional(NullableString),
    allocationMode: Type.Optional(
      Type.Union([Type.Literal("capacity"), Type.Literal("projected")]),
    ),
    indirectHours: Type.Optional(Type.String()),
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    asOf: IsoDateSchema,
    workedHours: Type.String(),
    openingBalanceHours: Type.String(),
    projectedHours: Type.String(),
    internalCosts: Type.String(),
    directHourlyAllocation: NullableString,
    projectedHourlyAllocation: NullableString,
    items: Type.Array(EmployeeCostOverviewItemSchema),
    ownerPerformance: Type.Array(EmployeeOwnerPerformanceSchema),
  },
  { additionalProperties: false },
);

export const EmployeeMonthlyCostSaveSchema = Type.Object(
  {
    operationId: UuidSchema,
    managementYear: Type.Integer({ minimum: 2000, maximum: 2200 }),
    month: Type.Integer({ minimum: 1, maximum: 12 }),
    costItem1: Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    costItem2: Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    costItem3: Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    costItem4: Type.Number({ minimum: 0, maximum: 9999999999.99 }),
    version: Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]),
  },
  { additionalProperties: false },
);

export interface EmployeeMonthlyCost {
  id: string;
  employeeId: string;
  employeeName: string;
  managementYear: number;
  month: number;
  costItem1: string;
  costItem2: string;
  costItem3: string;
  costItem4: string;
  totalCost: string;
  version: number;
  updatedAt: string;
}

export interface EmployeeMonthlyCostPage {
  managementYear: number;
  month: number | null;
  items: EmployeeMonthlyCost[];
}

export type EmployeeCostOverviewQuery = Static<
  typeof EmployeeCostOverviewQuerySchema
>;
export type EmployeeCostOverviewItem = Static<
  typeof EmployeeCostOverviewItemSchema
>;
export type EmployeeOwnerPerformance = Static<
  typeof EmployeeOwnerPerformanceSchema
>;
export type EmployeeCostOverview = Static<typeof EmployeeCostOverviewSchema>;

export const SupplierCreateSchema = Type.Object(
  {
    operationId: UuidSchema,
    businessName: Type.String({ minLength: 1, maxLength: 240 }),
    ...ContactFields,
  },
  { additionalProperties: false },
);

export const SupplierUpdateSchema = Type.Object(
  {
    version: Type.Integer({ minimum: 1 }),
    businessName: Type.Optional(Type.String({ minLength: 1, maxLength: 240 })),
    ...ContactFields,
    status: Type.Optional(SupplierSchema.properties.status),
  },
  { additionalProperties: false, minProperties: 2 },
);

export const SupplierPayablesQuerySchema = Type.Object(
  {
    managementYear: Type.Integer({ minimum: 2000, maximum: 2100 }),
  },
  { additionalProperties: false },
);

export interface SupplierPayableDocument {
  id: string;
  invoiceNumber: string | null;
  description: string | null;
  documentDate: string;
  dueDate: string | null;
  amountGross: string;
  paidGross: string;
  residualGross: string;
  locked: boolean;
  paymentStatus: "unpaid" | "partial";
}

export interface SupplierPayableRow {
  key: string;
  externalSupplierId: string | null;
  businessName: string;
  documentCount: number;
  amountGross: string;
  paidGross: string;
  residualGross: string;
  firstDueDate: string | null;
  paymentStatus: "unpaid" | "partial";
  documents: SupplierPayableDocument[];
}

export interface SupplierPayablesOverview {
  managementYear: number;
  source: "fic-cache";
  lastSyncAt: string | null;
  documentCount: number;
  supplierCount: number;
  amountGross: string;
  paidGross: string;
  residualGross: string;
  items: SupplierPayableRow[];
}

export type DirectoryQuery = Static<typeof DirectoryQuerySchema>;
export type DirectoryReferenceQuery = Static<
  typeof DirectoryReferenceQuerySchema
>;
export type Client = Static<typeof ClientSchema>;
export type Administrator = Static<typeof AdministratorSchema>;
export type Commercial = Static<typeof CommercialSchema>;
export type AdministratorCreate = Static<typeof AdministratorCreateSchema>;
export type CommercialCreate = Static<typeof CommercialCreateSchema>;
export type DirectoryReferenceStatusUpdate = Static<
  typeof DirectoryReferenceStatusUpdateSchema
>;
export type FicClientProfile = Static<typeof FicClientProfileSchema>;
export type Condominium = Static<typeof CondominiumSchema>;
export type Employee = Static<typeof EmployeeSchema>;
export type Supplier = Static<typeof SupplierSchema>;
export type DocumentRecord = Static<typeof DocumentSchema>;
export type ClientCreate = Static<typeof ClientCreateSchema>;
export type ClientUpdate = Static<typeof ClientUpdateSchema>;
export type ClientDelete = Static<typeof ClientDeleteSchema>;
export type CondominiumCreate = Static<typeof CondominiumCreateSchema>;
export type CondominiumUpdate = Static<typeof CondominiumUpdateSchema>;
export type CondominiumDelete = Static<typeof CondominiumDeleteSchema>;
export type EmployeeCreate = Static<typeof EmployeeCreateSchema>;
export type EmployeeUpdate = Static<typeof EmployeeUpdateSchema>;
export type EmployeeMonthlyCostQuery = Static<
  typeof EmployeeMonthlyCostQuerySchema
>;
export type EmployeeMonthlyCostSave = Static<
  typeof EmployeeMonthlyCostSaveSchema
>;
export type SupplierCreate = Static<typeof SupplierCreateSchema>;
export type SupplierUpdate = Static<typeof SupplierUpdateSchema>;
export type SupplierPayablesQuery = Static<typeof SupplierPayablesQuerySchema>;
export interface SubcontractorDirectoryItem {
  id: string;
  name: string;
  supplierId: string | null;
  categories: string[];
}
